/**
 * Word (.docx) Template Service
 */

import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { renderAsync as renderDocxAsync } from "docx-preview";
import type { FieldFormattingConfig } from "@/utils/formatter";
import {
  extractDocxMarkers,
  repairSplitMarkers,
  normalizeBracketDelimiters,
  SUM_RE,
  TEXT_RE,
} from "@/utils/docxMarker";
import type { DocxMarkerField } from "@/utils/docxMarker";
import { extractBody, findSectionRanges } from "@/utils/docxSections";
import { resolveFieldValue } from "@/utils/fieldResolve";
import { numberToVietnameseWords } from "@/utils/vietnameseNumberToWords";
import { applyFieldLayoutRules, FOOTER_SAFE_PX } from "@/utils/fieldLayout";
import { inspectDocxMedia } from "@/utils/docxMedia";

export async function renderDocxPreview(
  docxBuffer: ArrayBuffer | Uint8Array,
  container: HTMLElement
): Promise<void> {
  container.innerHTML = "";
  try {
    await renderDocxAsync(docxBuffer, container, undefined, {
      inWrapper: true,
      ignoreWidth: false,
      ignoreHeight: false,
      // docx-preview renders footer parts as inline content mid-page; the app
      // injects its own fixed footer band (ensureFooterZone → .docx-page-footer)
      // at the bottom of every page, so disable the footer part render to avoid
      // a duplicate "Trang …" that sits in the middle of the page.
      renderFooters: false,
    });

    // Fix overflow clipping for landscape & wide docx pages
    const wrapper = container.querySelector(".docx-wrapper") as HTMLElement | null;
    if (wrapper) {
      wrapper.style.display = "flex";
      wrapper.style.flexDirection = "column";
      wrapper.style.alignItems = "center";
      wrapper.style.minWidth = "100%";
      wrapper.style.background = "transparent";
      wrapper.style.padding = "12px";
    }
    // Strict Page & Element Containment with Native Page Margins
    const sections = container.querySelectorAll("section.docx");
    sections.forEach((sec) => {
      const el = sec as HTMLElement;
      el.style.boxShadow = "0 10px 25px -5px rgba(0, 0, 0, 0.3)";
      el.style.margin = "0 auto 16px auto";
      el.style.boxSizing = "border-box";
      el.style.maxWidth = "100%";
      el.style.overflow = "hidden";
      el.style.position = "relative";
      // Keep docx-preview's native page margins (from <w:pgMar>); only ensure min padding-bottom for footer
      const currentPadBottom = parseFloat(el.style.paddingBottom || "0") || 0;
      if (currentPadBottom < FOOTER_SAFE_PX + 12) {
        el.style.paddingBottom = `${FOOTER_SAFE_PX + 12}px`;
      }
    });

    // Enforce 100% max-width containment on child elements while respecting drawings and textboxes
    const allChildElements = container.querySelectorAll("section.docx *");
    allChildElements.forEach((child) => {
      const el = child as HTMLElement;

      // Do not crush or shift textboxes, drawings, or shape contents (e.g. top-right page boxes like 2/2)
      if (
        el.closest(".docx-drawing, .drawing, .shape, [data-docx-drawing], svg, foreignObject, .docx-textbox") ||
        el.classList?.contains("docx-drawing") ||
        el.classList?.contains("drawing") ||
        el.classList?.contains("shape") ||
        el.classList?.contains("docx-textbox")
      ) {
        return;
      }

      el.style.maxWidth = "100%";
      el.style.boxSizing = "border-box";

      if (el.tagName === "TABLE") {
        el.style.maxWidth = "100%";
        el.style.borderCollapse = "collapse";
        // Preserve table's natural proportional width; ensure full width if not a narrow box
        if (!el.style.width || el.style.width === "auto" || parseFloat(el.style.width) > 500) {
          el.style.width = "100%";
        }
      }

      if (el.tagName === "TR") {
        el.style.height = "auto";
        el.style.minHeight = "auto";
      }

      if (el.tagName === "TD" || el.tagName === "TH") {
        el.style.maxWidth = "100%";
        el.style.height = "auto";
        el.style.minHeight = "auto";
        el.style.padding = "4px 6px";
        el.style.fontSize = "11.5px";
        el.style.lineHeight = "1.32";
        el.style.verticalAlign = "middle";
        el.style.textAlign = "center";
        el.style.overflow = "visible";
        el.style.wordBreak = "break-word";
        el.style.overflowWrap = "break-word";
        el.style.whiteSpace = "normal";

        // Thin & crisp border normalization: prevent heavy/thick 2-3px double borders
        const s = el.style;
        if (s.borderTopStyle === "solid") s.borderTopWidth = "1px";
        if (s.borderBottomStyle === "solid") s.borderBottomWidth = "1px";
        if (s.borderLeftStyle === "solid") s.borderLeftWidth = "1px";
        if (s.borderRightStyle === "solid") s.borderRightWidth = "1px";
        if (s.borderStyle === "solid") s.borderWidth = "1px";

        if (el.tagName === "TH") {
          el.style.fontWeight = "700";
        }
      }

      // Paragraph styling: reset margins and padding inside table cells so text stays perfectly centered
      if (el.tagName === "P") {
        el.style.lineHeight = "1.32";
        el.style.minHeight = "auto";
        if (el.closest("td, th")) {
          el.style.margin = "0";
          el.style.padding = "0";
          el.style.lineHeight = "inherit";
          el.style.textAlign = "center";
        }
      }

      if (el.tagName === "IMG") {
        // Keep logos / embedded images readable; do not force tiny table fonts.
        el.style.height = "auto";
        el.style.maxWidth = el.style.maxWidth || "100%";
        el.style.objectFit = "contain";
        el.style.wordBreak = "normal";
        el.style.overflowWrap = "normal";
      }

      // docx-preview wraps drawings; avoid table text rules crushing them.
      if (
        el.classList?.contains("docx-drawing") ||
        el.classList?.contains("drawing") ||
        el.getAttribute("data-docx-drawing") != null
      ) {
        el.style.wordBreak = "normal";
        el.style.overflowWrap = "normal";
        el.style.whiteSpace = "normal";
      }
    });

    // Identifier nowrap + font shrink; content padding / height fit
    applyFieldLayoutRules(container);
    autoFitSectionToPage(container);
    cleanTemplateLoopMarkers(container);
  } catch (err) {
    console.error("[DocxService] Failed to render docx preview:", err);
    container.innerHTML = `<div class="p-4 text-center text-red-500 text-sm">Khởi tạo bản xem trước thất bại.</div>`;
  }
}

function getTargetSectionHeightPx(sec: HTMLElement): number {
  const inlineMinH = sec.style.minHeight;
  if (inlineMinH) {
    if (inlineMinH.includes("mm")) return (parseFloat(inlineMinH) / 25.4) * 96;
    if (inlineMinH.includes("pt")) return (parseFloat(inlineMinH) / 72) * 96;
    if (inlineMinH.includes("px")) return parseFloat(inlineMinH);
  }

  const attrStyle = sec.getAttribute("style") || "";
  const match = attrStyle.match(/min-height:\s*([0-9.]+)(mm|pt|px)/i);
  if (match) {
    const num = parseFloat(match[1]);
    const unit = match[2].toLowerCase();
    if (unit === "mm") return (num / 25.4) * 96;
    if (unit === "pt") return (num / 72) * 96;
    if (unit === "px") return num;
  }

  const widthStr = sec.style.width || attrStyle;
  if (widthStr.includes("210mm") && !widthStr.includes("297mm") && !attrStyle.includes("297mm")) {
    // A5 Landscape (210mm wide x 148mm high)
    return (148 / 25.4) * 96; // ≈559px
  }
  if (widthStr.includes("148mm") && !widthStr.includes("210mm")) {
    // A5 Portrait (148mm wide x 210mm high)
    return (210 / 25.4) * 96; // ≈794px
  }
  if (widthStr.includes("105mm")) {
    // A6 Portrait (105mm wide x 148mm high)
    return (148 / 25.4) * 96; // ≈559px
  }

  return sec.clientHeight || 1123;
}

/**
 * Detects if content inside any section.docx overflows the physical paper height
 * (especially when users select smaller or landscape paper sizes like A5 Landscape / A6).
 *
 * Proportionally compacts heading margins, title font sizes, table cell padding,
 * and cell typography directly on DOM elements without using CSS zoom/transform,
 * guaranteeing natural word spacing, crisp borders, and zero text-border collision.
 */
export function autoFitSectionToPage(container: HTMLElement): void {
  const sections = Array.from(container.querySelectorAll("section.docx")) as HTMLElement[];

  sections.forEach((sec) => {
    const targetHeightPx = getTargetSectionHeightPx(sec);
    if (!targetHeightPx || targetHeightPx <= 0) return;

    sec.style.boxSizing = "border-box";
    const contentH = sec.scrollHeight;

    // Check if content exceeds target physical paper height
    if (contentH > targetHeightPx - 5) {
      // Calculate how tight the content needs to be
      const overflowRatio = contentH / targetHeightPx; // e.g. 1.15 to 1.5

      // 1. Compact all heading / title paragraphs
      const paras = Array.from(sec.querySelectorAll("p")) as HTMLElement[];
      paras.forEach((p) => {
        if (
          !p.closest("table") &&
          !p.closest(".docx-drawing, .drawing, .shape, [data-docx-drawing], svg, foreignObject, .docx-textbox")
        ) {
          p.style.marginTop = "1px";
          p.style.marginBottom = "2px";
          p.style.lineHeight = "1.2";
          const currentFont = parseFloat(window.getComputedStyle(p).fontSize) || 14;
          if (overflowRatio > 1.2 && currentFont > 15) {
            p.style.fontSize = `${Math.max(13, currentFont * 0.85)}px`;
          }
        }
      });

      // 2. Format table cells with generous breathing room
      const tables = Array.from(sec.querySelectorAll("table")) as HTMLTableElement[];
      tables.forEach((tbl) => {
        tbl.style.marginTop = "4px";
        tbl.style.marginBottom = "4px";
        tbl.style.borderCollapse = "collapse";
        
        const rows = Array.from(tbl.querySelectorAll("tr")) as HTMLTableRowElement[];
        rows.forEach((r) => {
          r.style.height = "auto";
          r.style.minHeight = "auto";
          r.style.maxHeight = "none";
        });

        const cells = Array.from(tbl.querySelectorAll("td, th")) as HTMLElement[];
        cells.forEach((c) => {
          c.style.height = "auto";
          c.style.minHeight = "auto";
          c.style.maxHeight = "none";
          c.style.verticalAlign = "middle";
          c.style.textAlign = "center";
          c.style.overflow = "visible";
          c.style.wordBreak = "break-word";
          c.style.whiteSpace = "normal";

          // Generous breathing room so descenders (g, y, p) and ascenders are completely clear of borders
          c.style.paddingTop = "5px";
          c.style.paddingBottom = "5px";
          c.style.paddingLeft = "6px";
          c.style.paddingRight = "6px";
          c.style.lineHeight = "1.35";
          c.style.fontSize = "11px";

          const cellParas = Array.from(c.querySelectorAll("p")) as HTMLElement[];
          cellParas.forEach((p) => {
            p.style.margin = "0";
            p.style.padding = "0";
            p.style.lineHeight = "inherit";
            p.style.textAlign = "center";
          });
        });
      });
    }
  });
}

/**
 * Detect table loop rows containing docxtemplater loop tags (e.g., {{#items}}, {{/items}},
 * {{#danh_sach}}, etc.).
 *
 * For non-technical user clarity during Review Canvas:
 * 1. Hides/strips raw technical loop control tags `{{#...}}` and `{{/...}}` from cell text.
 * 2. Leaves clean data field placeholders like `{{STT}}`, `{{Mã hàng}}` visible.
 * 3. Highlights the table loop row visually with a soft tint and tooltip.
 */
export function cleanTemplateLoopMarkers(container: HTMLElement): void {
  const rows = Array.from(container.querySelectorAll("table tr")) as HTMLTableRowElement[];

  rows.forEach((row) => {
    const rowText = row.textContent ?? "";
    if (/\{\{#[^{}]*\}\}|\{\{\/[^{}]*\}\}/.test(rowText)) {
      row.style.backgroundColor = "rgba(59, 130, 246, 0.05)";
      row.style.borderLeft = "3px solid #3b82f6";
      row.setAttribute("data-loop-row", "true");
      row.setAttribute("title", "Hàng lặp bảng chi tiết (Tự động lặp theo dữ liệu Excel)");

      const cells = Array.from(row.querySelectorAll("td, th")) as HTMLElement[];
      cells.forEach((cell) => {
        const walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT);
        let currentNode = walker.nextNode();
        while (currentNode) {
          if (currentNode.nodeValue && /\{\{#[^{}]*\}\}|\{\{\/[^{}]*\}\}/.test(currentNode.nodeValue)) {
            currentNode.nodeValue = currentNode.nodeValue.replace(/\{\{#[^{}]*\}\}|\{\{\/[^{}]*\}\}/g, "");
          }
          currentNode = walker.nextNode();
        }
      });
    }
  });
}

// ============================================================
// Placeholder fill ({{field}} / {{#items}}…{{/items}})
// ============================================================

export interface MarkerFillInput {
  /** Common/header fields (fields NOT inside a repeat loop). */
  headerFields: DocxMarkerField[];
  /** Item fields (fields inside a `{{#…}} … {{/…}}` loop). */
  itemFields: DocxMarkerField[];
  /** Per-field display formatting (date / number / currency / percent / text). */
  formatByField?: Record<string, FieldFormattingConfig>;
  /**
   * Detail rows to list on each Word section, keyed by section index. A section
   * without an entry lists the whole group. This is how a page-level condition
   * narrows its own table (quarantine sheet) without touching the other pages.
   */
  itemRowsBySection?: Record<number, Record<string, unknown>[]>;
}

/**
 * Parse a placeholder-based DOCX file: extract field placeholders & loop
 * ranges and keep the raw file data so it can be filled later.
 */
export function parseMarkerDocxFile(
  fileBuffer: ArrayBuffer | Uint8Array,
  fileName: string
): {
  fileName: string;
  fileData: ArrayBuffer | Uint8Array;
  result: ReturnType<typeof extractDocxMarkers>;
  /** Embedded logos / images found in the package. */
  media: ReturnType<typeof inspectDocxMedia>;
} {
  const zip = new PizZip(fileBuffer);
  let docXml = zip.files["word/document.xml"]?.asText() ?? "";
  // Normalize [[…]] to {{…}} so templates written with either delimiter work.
  docXml = normalizeBracketDelimiters(docXml);
  const repaired = repairSplitMarkers(docXml);
  if (repaired.repairedCount > 0) {
    docXml = repaired.xml;
    zip.file("word/document.xml", docXml);
    fileBuffer = zip.generate({
      type: "uint8array",
      mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
  }
  const result = extractDocxMarkers(docXml);
  if (repaired.repairedCount > 0) {
    result.warnings = [
      ...result.warnings,
      `Đã ghép ${repaired.repairedCount} đoạn có placeholder bị Word tách khi chèn logo/ảnh.`,
    ];
  }
  const media = inspectDocxMedia(fileBuffer);
  if (media.warnings.length > 0) {
    result.warnings = [...result.warnings, ...media.warnings];
  }
  return { fileName, fileData: fileBuffer, result, media };
}

/** Keep first occurrence of each field name (a label may repeat across pages). */
function dedupeFieldsByName<T extends { fieldName: string }>(fields: T[]): T[] {
  return Array.from(new Map(fields.map((f) => [f.fieldName, f])).values());
}

/** A `{{#name}}…{{/name}}` loop pair with token offsets. */
interface LoopPair {
  startOffset: number;
  startEnd: number;
  endOffset: number;
  endEnd: number;
}

/**
 * Find every `{{#name}} … {{/name}}` loop pair in document order. Names need
 * not match between open and close when there is exactly one of each; when
 * multiple loops share a name they are paired in order of appearance.
 */
function findLoopPairs(docXml: string): { pairs: LoopPair[]; errors: string[] } {
  const errors: string[] = [];
  const starts: { offset: number; end: number; name: string }[] = [];
  const ends: { offset: number; end: number; name: string }[] = [];
  const re = /\{\{([#/])([^{}]*)\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(docXml)) !== null) {
    const kind = m[1];
    const offset = m.index;
    const end = m.index + m[0].length;
    const name = m[2].trim();
    if (kind === "#") starts.push({ offset, end, name });
    else ends.push({ offset, end, name });
  }

  if (starts.length !== ends.length) {
    errors.push(
      `Vùng repeat không đầy đủ marker bắt đầu hoặc kết thúc (${starts.length} mở / ${ends.length} đóng).`
    );
  }

  const pairs: LoopPair[] = [];
  const usedStarts = new Set<number>();
  const usedEnds = new Set<number>();

  // Pair by matching name first (stable for multiple loops), then by order.
  for (const s of starts) {
    if (usedStarts.has(s.offset)) continue;
    const candidates = ends.filter((e) => !usedEnds.has(e.offset) && e.name === s.name);
    const best =
      candidates.length > 0
        ? candidates[0]
        : ends.find((e) => !usedEnds.has(e.offset));
    if (!best) continue;
    if (best.offset <= s.offset) {
      errors.push(`Vùng repeat "${s.name || "?"}" có marker đóng đứng trước marker mở.`);
      continue;
    }
    pairs.push({ startOffset: s.offset, startEnd: s.end, endOffset: best.offset, endEnd: best.end });
    usedStarts.add(s.offset);
    usedEnds.add(best.offset);
  }

  return { pairs, errors };
}

/**
 * Render a placeholder-based template against a group of rows.
 *
 * - `{{fieldName}}` placeholders are filled from the group (header fields → the
 *   first non-empty group value; item fields → per row).
 * - `{{#items}} … {{/items}}` loops are renamed per Word section to
 *   `{{#items_N}} … {{/items_N}}` so each physical page can list its own rows
 *   (itemRowsBySection); the sample row is duplicated per row of the group.
 * - `⟦SUM:fieldName⟧` is replaced with the column total over the section rows.
 * - `⟦PAGE⟧` / `⟦TOTAL⟧` pass through untouched (handled at render/export).
 */
export function fillDocxTemplateWithMarkers(
  fileBuffer: ArrayBuffer | Uint8Array,
  group: {
    rows: Record<string, unknown>[];
    headerValues: Record<string, unknown>;
  },
  input: MarkerFillInput
): Uint8Array {
  const zip = new PizZip(fileBuffer);
  let docXml = zip.files["word/document.xml"]?.asText() ?? "";
  // Normalize [[…]] to {{…}} so templates written with either delimiter work.
  docXml = normalizeBracketDelimiters(docXml);
  // Logo inserts frequently split `{{…}}` across runs — repair before any offset work.
  docXml = repairSplitMarkers(docXml).xml;

  if (!/{{[^{}]*}}/.test(docXml)) {
    throw new Error("Template không chứa placeholder {{tên}}.");
  }

  // 1. Re-parse fields on the (possibly) repaired XML for fresh offsets.
  const reparsed = extractDocxMarkers(docXml);
  const columnByField = new Map<string, string>();
  for (const f of [...input.headerFields, ...input.itemFields]) {
    columnByField.set(f.fieldName, f.excelColumn ?? "");
  }
  // Stable lists for DATA binding.
  const headerFields = dedupeFieldsByName(
    reparsed.fields
      .filter((f) => !f.inRepeat)
      .map((f) => ({ ...f, excelColumn: columnByField.get(f.fieldName) ?? "" }))
  );
  const itemFields = dedupeFieldsByName(
    reparsed.fields
      .filter((f) => f.inRepeat)
      .map((f) => ({ ...f, excelColumn: columnByField.get(f.fieldName) ?? "" }))
  );

  // 2. Build replacement plan (descending offsets).
  interface Replacement {
    offset: number;
    oldLen: number;
    text: string;
  }
  const replacements: Replacement[] = [];
  const occupied = new Set<number>(); // bytes already claimed by another replacement

  const pushReplacement = (offset: number, oldLen: number, text: string) => {
    for (let i = 0; i < oldLen; i++) {
      if (occupied.has(offset + i)) return;
    }
    for (let i = 0; i < oldLen; i++) occupied.add(offset + i);
    replacements.push({ offset, oldLen, text });
  };

  // Each loop is renamed per section so a page whose condition filtered the
  // rows shows only those rows while its neighbours keep the full group.
  const bodySplit = extractBody(docXml);
  const sectionRanges = bodySplit ? findSectionRanges(bodySplit.body) : [];
  const bodyOffset = bodySplit ? bodySplit.prefix.length : 0;
  const sectionIndexAt = (offset: number): number => {
    const relative = offset - bodyOffset;
    const index = sectionRanges.findIndex((r) => relative >= r.start && relative < r.end);
    return index === -1 ? 0 : index;
  };

  const { pairs: loopPairs, errors: loopErrors } = findLoopPairs(docXml);
  if (loopErrors.length > 0) {
    throw new Error(loopErrors.join(" "));
  }

  loopPairs.forEach((p) => {
    const sectionIndex = sectionIndexAt(p.startOffset);
    const loopName = `items_${sectionIndex}`;
    pushReplacement(p.startOffset, p.startEnd - p.startOffset, `{{#${loopName}}}`);
    pushReplacement(p.endOffset, p.endEnd - p.endOffset, `{{/${loopName}}}`);
  });

  // ⟦SUM:fieldName⟧ / {{SUM:fieldName}} — total a field over the rows of the
  // section that contains the marker (mirrors how per-section item arrays are
  // built), so a TC / tổng cộng row in one page sums only that page's data.
  {
    const colByField = new Map<string, string>();
    [...headerFields, ...itemFields, ...input.headerFields, ...input.itemFields].forEach((f) => {
      if (f.excelColumn) colByField.set(f.fieldName, f.excelColumn);
    });
    const sumCache = new Map<string, number>(); // key: `${sectionIndex}:${fieldName}`
    SUM_RE.lastIndex = 0;
    let sm: RegExpExecArray | null;
    // Accept both the legacy ⟦SUM:…⟧ and the canonical {{SUM:…}} form.
    const sumRe = /(?:⟦SUM:([^⟧]*)⟧|\{\{SUM:([^}]*)\}\})/g;
    while ((sm = sumRe.exec(docXml)) !== null) {
      const fieldName = (sm[1] ?? sm[2] ?? "").trim();
      if (!fieldName) continue;
      const sectionIndex = sectionIndexAt(sm.index);
      const key = `${sectionIndex}:${fieldName}`;
      let total: number;
      if (sumCache.has(key)) {
        total = sumCache.get(key)!;
      } else {
        const sourceRows = input.itemRowsBySection?.[sectionIndex] ?? group.rows;
        const col = colByField.get(fieldName) ?? fieldName;
        total = sourceRows.reduce((acc, row) => {
          const raw = row?.[col];
          const num = typeof raw === "number" ? raw : Number(String(raw ?? "").replace(/[^\d.\-]/g, ""));
          return acc + (Number.isFinite(num) ? num : 0);
        }, 0);
        sumCache.set(key, total);
      }
      const text = Number.isInteger(total) ? String(total) : String(Math.round(total * 1000) / 1000);
      pushReplacement(sm.index, sm[0].length, text);
    }
  }

  // ⟦TEXT:fieldName⟧ / {{TEXT:fieldName}} — number-to-words (accounting):
  //   ⟦TEXT:Tổng tiền⟧        → words of the header/single value
  //   ⟦TEXT:SUM:Số lượng⟧     → words of the column total over the section
  // Produces e.g. "một triệu hai trăm năm mươi nghìn" for invoices.
  {
    const colByField = new Map<string, string>();
    [...headerFields, ...itemFields, ...input.headerFields, ...input.itemFields].forEach((f) => {
      if (f.excelColumn) colByField.set(f.fieldName, f.excelColumn);
    });
    const textSumCache = new Map<string, number>();
    TEXT_RE.lastIndex = 0;
    let tm: RegExpExecArray | null;
    const textRe = /(?:⟦TEXT:([^⟧]*)⟧|\{\{TEXT:([^}]*)\}\})/g;
    while ((tm = textRe.exec(docXml)) !== null) {
      const spec = (tm[1] ?? tm[2] ?? "").trim();
      if (!spec) continue;
      const sectionIndex = sectionIndexAt(tm.index);
      let words = "";
      if (/^SUM:/i.test(spec)) {
        // Words of a column total over the rows of this section.
        const fieldName = spec.slice(4).trim();
        if (fieldName) {
          const key = `${sectionIndex}:${fieldName}`;
          let total: number;
          if (textSumCache.has(key)) {
            total = textSumCache.get(key)!;
          } else {
            const sourceRows = input.itemRowsBySection?.[sectionIndex] ?? group.rows;
            const col = colByField.get(fieldName) ?? fieldName;
            total = sourceRows.reduce((acc, row) => {
              const raw = row?.[col];
              const num = typeof raw === "number" ? raw : Number(String(raw ?? "").replace(/[^\d.\-]/g, ""));
              return acc + (Number.isFinite(num) ? num : 0);
            }, 0);
            textSumCache.set(key, total);
          }
          words = numberToVietnameseWords(total);
        }
      } else {
        // Words of a single field value (header field or the first row value).
        const fieldName = spec;
        const col = colByField.get(fieldName) ?? fieldName;
        const resolved = resolveFieldValue(group.headerValues, fieldName, {
          excelColumn: col,
          autoDateByName: false,
        });
        const raw =
          resolved.value !== "" ? resolved.value : (group.headerValues[col] ?? group.headerValues[fieldName]);
        words = numberToVietnameseWords(raw);
      }
      if (words) pushReplacement(tm.index, tm[0].length, words);
    }
  }

  replacements.sort((a, b) => b.offset - a.offset);
  for (const r of replacements) {
    docXml = docXml.slice(0, r.offset) + r.text + docXml.slice(r.offset + r.oldLen);
  }

  // No control tokens should remain (SUM/TEXT replaced; PAGE/TOTAL kept).
  if (docXml.includes("⟦SUM:") || /\{\{SUM:/.test(docXml)) {
    throw new Error("Không thay hết được ⟦SUM:…⟧ / {{SUM:…}} — hãy kiểm tra template.");
  }
  if (docXml.includes("⟦TEXT:") || /\{\{TEXT:/.test(docXml)) {
    throw new Error("Không thay hết được ⟦TEXT:…⟧ / {{TEXT:…}} — hãy kiểm tra template.");
  }

  zip.file("word/document.xml", docXml);

  // 3. Build data object via the shared resolve path (preview/PDF/print).
  const data: Record<string, unknown> = {};
  const formatOf = (fieldName: string) => input.formatByField?.[fieldName];

  for (const f of headerFields) {
    const resolved = resolveFieldValue(group.headerValues, f.fieldName, {
      excelColumn: f.excelColumn,
      format: formatOf(f.fieldName),
      autoDateByName: true,
    });
    data[f.fieldName] = resolved.value;
  }

  // Auto STT for the first item field whose label looks like a sequence number.
  const sttField = itemFields.find(
    (f) => /^stt$/i.test(f.normalizedName) || /\bstt\b/i.test(f.fieldName)
  );

  const buildItems = (sourceRows: Record<string, unknown>[]) =>
    sourceRows.map((row, idx) => {
      const item: Record<string, unknown> = {};
      for (const f of itemFields) {
        const resolved = resolveFieldValue(row, f.fieldName, {
          excelColumn: f.excelColumn,
          format: formatOf(f.fieldName),
          autoDateByName: true,
        });
        item[f.fieldName] = resolved.value;
      }
      // Numbering restarts on each page, over the rows that page actually lists.
      if (sttField) item[sttField.fieldName] = idx + 1;
      return item;
    });

  data.items = buildItems(group.rows);
  new Set(loopPairs.map((p) => `items_${sectionIndexAt(p.startOffset)}`)).forEach((name) => {
    const sectionIndex = Number(name.slice("items_".length));
    data[name] = buildItems(input.itemRowsBySection?.[sectionIndex] ?? group.rows);
  });

  let doc: Docxtemplater;
  try {
    doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      delimiters: { start: "{{", end: "}}" },
      // Keep Word-style page counters {{PAGE}} / {{NUMPAGES}} (and legacy
      // {{TOTAL}}) as literal text through the fill so the renderer can
      // substitute real page numbers afterwards. Without this, Docxtemplater
      // drops them (unknown tag → empty) before preview/export numbering.
      nullGetter: (part) => {
              // Docxtemplater exposes the placeholder name on `part.value` (not `.tag`).
              const tag = String(part?.value ?? part?.tag ?? "").toUpperCase().trim();
              if (tag === "PAGE") return "{{PAGE}}";
              if (tag === "NUMPAGES" || tag === "TOTAL") return "{{NUMPAGES}}";
              return "";
            },
    });
    doc.render(data);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (/malformed xml/i.test(msg)) {
      throw new Error(
        "Template XML bị lỗi khi bind (thường gặp khi chèn logo làm Word gộp ô / tách placeholder). " +
          "Hãy giữ {{tên}} nằm nguyên trong cùng một đoạn văn bản, không để logo cắt giữa placeholder."
      );
    }
    throw err;
  }

  return doc.getZip().generate({
    type: "uint8array",
    mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
}